/**
 * useConflictDetection
 * React hook for detecting and handling concurrent edit conflicts
 * Feature: User Management (002-user-management) - Phase 3
 */

import { useState, useCallback } from 'react';

/**
 * Hook for managing conflict detection state
 * @returns {Object} Conflict detection utilities
 */
export const useConflictDetection = () => {
  const [conflicts, setConflicts] = useState([]);
  const [showConflictNotification, setShowConflictNotification] = useState(false);

  /**
   * Add a conflict warning
   * @param {Object} conflict - Conflict details from API
   * @param {number} sessionId - Session ID
   * @param {string} sessionName - Session name
   */
  const addConflict = useCallback((conflict, sessionId, sessionName) => {
    if (!conflict) return;

    const conflictEntry = {
      sessionId,
      sessionName,
      message: conflict.message || 'Concurrent edit detected',
      dbUpdatedAt: conflict.dbUpdatedAt,
      clientUpdatedAt: conflict.clientUpdatedAt,
      timestamp: new Date().toISOString()
    };

    setConflicts(prev => [...prev, conflictEntry]);
    setShowConflictNotification(true);
  }, []);

  /**
   * Clear all conflicts
   */
  const clearConflicts = useCallback(() => {
    setConflicts([]);
    setShowConflictNotification(false);
  }, []);

  /**
   * Remove a specific conflict
   * @param {number} sessionId - Session ID to remove conflict for
   */
  const removeConflict = useCallback((sessionId) => {
    setConflicts(prev => prev.filter(c => c.sessionId !== sessionId));
    
    // Hide notification if no more conflicts
    setConflicts(prev => {
      if (prev.length === 0) {
        setShowConflictNotification(false);
      }
      return prev;
    });
  }, []);

  /**
   * Dismiss the conflict notification
   */
  const dismissNotification = useCallback(() => {
    setShowConflictNotification(false);
  }, []);

  /**
   * Check if there are any active conflicts
   */
  const hasConflicts = conflicts.length > 0;

  /**
   * Get conflicts for a specific session
   * @param {number} sessionId - Session ID
   * @returns {Array} Conflicts for the session
   */
  const getConflictsForSession = useCallback((sessionId) => {
    return conflicts.filter(c => c.sessionId === sessionId);
  }, [conflicts]);

  return {
    conflicts,
    showConflictNotification,
    hasConflicts,
    addConflict,
    clearConflicts,
    removeConflict,
    dismissNotification,
    getConflictsForSession
  };
};

export default useConflictDetection;
